import React, { useCallback, useEffect, useState } from 'react';
import Pagination from './Pagination';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import {
  createAlert,
  deleteAlert,
  getMetadata,
  listAlerts,
  listDispatches,
  previewAlert,
  sendAlertNow,
  setAlertEnabled,
  updateAlert,
} from '../api/analyticsApi';

// ── Local style consts (mirrors VisitBookingsManagement / BrandstoreManagement) ──
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.02em', borderBottom: '1.5px solid #E5E7EB' };
const td = { padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' };
const btnPrimary = { padding: '8px 14px', background: '#F29213', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { padding: '8px 14px', background: '#fff', color: '#374151', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 5px', lineHeight: 1 };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 };
const input = { width: '100%', padding: '9px 11px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };
const chip = { display: 'inline-block', background: '#F3F4F6', color: '#374151', borderRadius: 999, padding: '2px 9px', fontSize: 11, marginRight: 4, marginBottom: 3 };

const STATUS_STYLE = {
  SENT: { color: '#166534', bg: '#dcfce7' },
  PARTIAL: { color: '#92400e', bg: '#fef3c7' },
  SKIPPED: { color: '#4b5563', bg: '#f3f4f6' },
  FAILED: { color: '#991b1b', bg: '#fee2e2' },
  RUNNING: { color: '#1e40af', bg: '#dbeafe' },
};

const EMPTY_FORM = {
  name: '',
  alertType: 'BUSINESS_DIGEST',
  frequency: 'DAILY',
  sections: [],
  recipients: [],
  sendHour: 8,
  sendMinute: 0,
  timeZone: 'Asia/Kolkata',
  enabled: true,
};

const pad = (n) => String(n).padStart(2, '0');
const errorFrom = (err, fallback) => err?.response?.data?.message || fallback;
const fmtDateTime = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '—');

// ── Create / edit dialog ──────────────────────────────────────────────────────
function AlertDialog({ alert, metadata, onClose, onSaved }) {
  const { addToast } = useToast();
  const isEdit = Boolean(alert?.id);
  const [form, setForm] = useState(alert?.id ? { ...EMPTY_FORM, ...alert } : EMPTY_FORM);
  const [recipientDraft, setRecipientDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleSection = (value) =>
    set('sections', form.sections.includes(value)
      ? form.sections.filter((s) => s !== value)
      : [...form.sections, value]);

  const addRecipient = () => {
    const value = recipientDraft.trim().toLowerCase();
    if (!value) return;
    if (!form.recipients.includes(value)) set('recipients', [...form.recipients, value]);
    setRecipientDraft('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.recipients.length === 0) {
      addToast('Add at least one recipient.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await updateAlert(alert.id, form);
      else await createAlert(form);
      addToast(isEdit ? 'Alert updated.' : 'Alert created.', 'success');
      onSaved();
    } catch (err) {
      addToast(errorFrom(err, 'Could not save the alert.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 10, padding: 24, width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ margin: '0 0 18px', fontSize: 18, fontWeight: 800, color: '#111827' }}>
          {isEdit ? 'Edit alert' : 'New alert'}
        </h2>

        <div style={{ marginBottom: 16 }}>
          <label style={label}>Name</label>
          <input style={input} value={form.name} onChange={(e) => set('name', e.target.value)} required placeholder="Daily Business Report" />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Type</label>
            <select style={input} value={form.alertType} onChange={(e) => set('alertType', e.target.value)}>
              {(metadata.alertTypes || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Cadence</label>
            <select style={input} value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
              {(metadata.frequencies || []).map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
        </div>

        {form.alertType === 'THRESHOLD_ALERT' && (
          <p style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '9px 12px', borderRadius: 7, margin: '0 0 16px' }}>
            Threshold alerts only send when a metric moves sharply or flatlines. On a quiet period, nothing is sent.
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 90 }}>
            <label style={label}>Hour</label>
            <input style={input} type="number" min="0" max="23" value={form.sendHour} onChange={(e) => set('sendHour', Number(e.target.value))} />
          </div>
          <div style={{ width: 90 }}>
            <label style={label}>Minute</label>
            <input style={input} type="number" min="0" max="59" value={form.sendMinute} onChange={(e) => set('sendMinute', Number(e.target.value))} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Time zone</label>
            <input style={input} value={form.timeZone} onChange={(e) => set('timeZone', e.target.value)} />
          </div>
        </div>
        <p style={{ fontSize: 11, color: '#9CA3AF', margin: '-8px 0 16px' }}>
          Controls when this alert is sent. Reporting periods are always measured in India Standard Time.
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={label}>Sections</label>
          {(metadata.sections || []).map((s) => (
            <label key={s.value} style={{ display: 'flex', alignItems: 'center', marginBottom: 6, fontSize: 13, color: '#374151' }}>
              <input type="checkbox" checked={form.sections.includes(s.value)} onChange={() => toggleSection(s.value)} style={{ marginRight: 8 }} />
              {s.label}
            </label>
          ))}
          <p style={{ fontSize: 11, color: '#9CA3AF', margin: '6px 0 0' }}>
            Leave all unticked to include every section, including any added later.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={label}>Recipients</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={input}
              type="email"
              value={recipientDraft}
              onChange={(e) => setRecipientDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
              placeholder="name@eyantra.com"
            />
            <button type="button" style={btnGhost} onClick={addRecipient}>Add</button>
          </div>
          <div style={{ marginTop: 10 }}>
            {form.recipients.length === 0
              ? <span style={{ fontSize: 12, color: '#9CA3AF' }}>No recipients yet.</span>
              : form.recipients.map((r) => (
                <span key={r} style={chip}>
                  {r}
                  <button
                    type="button"
                    onClick={() => set('recipients', form.recipients.filter((x) => x !== r))}
                    style={{ ...iconBtn, color: '#9CA3AF', fontSize: 13, marginLeft: 4 }}
                    aria-label={`Remove ${r}`}
                  >×</button>
                </span>
              ))}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: '#374151', marginBottom: 20 }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} style={{ marginRight: 8 }} />
          Enabled
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" style={btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

// ── Preview modal ─────────────────────────────────────────────────────────────
function PreviewModal({ html, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 16, width: 760, maxWidth: '100%', height: '86vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>Preview</h2>
          <button style={btnGhost} onClick={onClose}>Close</button>
        </div>
        {/* iframe, not dangerouslySetInnerHTML: the email's CSS is written for mail clients and
            would otherwise bleed into the admin layout. */}
        <iframe title="Alert preview" srcDoc={html} style={{ flex: 1, width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8 }} />
      </div>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function AnalyticsReportsManagement() {
  const confirm = useConfirm();
  const { addToast } = useToast();

  const [alerts, setAlerts] = useState([]);
  const [metadata, setMetadata] = useState({ alertTypes: [], frequencies: [], sections: [] });
  const [dispatches, setDispatches] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [busyRow, setBusyRow] = useState(null);

  // Preview / send-now override. Scheduled runs are never affected — this exists so an alert can
  // be exercised against a period that actually has data, which matters on a sparse environment.
  const [testStart, setTestStart] = useState('');
  const [testEnd, setTestEnd] = useState('');

  const loadAlerts = useCallback(() => {
    setLoading(true);
    Promise.all([listAlerts(), getMetadata()])
      .then(([a, m]) => { setAlerts(a.data || []); setMetadata(m.data || {}); })
      .catch((err) => addToast(errorFrom(err, 'Could not load alerts.'), 'error'))
      .finally(() => setLoading(false));
  }, [addToast]);

  const loadDispatches = useCallback(() => {
    listDispatches(page, 20)
      .then((r) => { setDispatches(r.data.content || []); setTotalPages(r.data.totalPages || 0); })
      .catch(() => { setDispatches([]); setTotalPages(0); });
  }, [page]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);
  useEffect(() => { loadDispatches(); }, [loadDispatches]);

  const handleToggle = async (alert) => {
    try {
      await setAlertEnabled(alert.id, !alert.enabled);
      loadAlerts();
    } catch (err) {
      addToast(errorFrom(err, 'Could not change the alert.'), 'error');
    }
  };

  const handlePreview = async (alert) => {
    setBusyRow(alert.id);
    try {
      const r = await previewAlert(alert.id, testStart, testEnd);
      setPreviewHtml(typeof r.data === 'string' ? r.data : '');
    } catch (err) {
      addToast(errorFrom(err, 'Could not build the preview.'), 'error');
    } finally {
      setBusyRow(null);
    }
  };

  const handleSendNow = async (alert) => {
    const ok = await confirm({
      title: `Send "${alert.name}" now?`,
      message: `This emails ${alert.recipients.length} recipient(s) immediately, outside the schedule.`,
      confirmLabel: 'Send now',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;

    setBusyRow(alert.id);
    try {
      const r = await sendAlertNow(alert.id, testStart, testEnd);
      const tone = r.data.status === 'SENT' ? 'success' : r.data.status === 'SKIPPED' ? 'info' : 'warning';
      addToast(r.data.message || `Sent to ${r.data.sent} recipient(s).`, tone);
      loadDispatches();
      loadAlerts();
    } catch (err) {
      addToast(errorFrom(err, 'Could not send the alert.'), 'error');
    } finally {
      setBusyRow(null);
    }
  };

  const handleDelete = async (alert) => {
    const ok = await confirm({
      title: `Delete "${alert.name}"?`,
      message: 'The alert and its schedule are removed. Past dispatches stay in the log.',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteAlert(alert.id);
      addToast('Alert deleted.', 'success');
      loadAlerts();
    } catch (err) {
      addToast(errorFrom(err, 'Could not delete the alert.'), 'error');
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px', letterSpacing: '-0.5px' }}>Business Reports</h1>
          <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0, maxWidth: 720 }}>
            Scheduled email digests covering quotes, Brandstore, D2C and users, with week-on-week
            and month-on-month comparisons. Each alert reports on the previous completed period.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btnGhost} onClick={() => { loadAlerts(); loadDispatches(); }}>Refresh</button>
          <button style={btnPrimary} onClick={() => setEditing({})}>+ New Alert</button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1.5px solid #E5E7EB', borderRadius: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Test over a specific period
        </span>
        <div>
          <label style={{ ...label, marginBottom: 2 }}>From</label>
          <input style={{ ...input, width: 150 }} type="date" value={testStart} onChange={(e) => setTestStart(e.target.value)} />
        </div>
        <div>
          <label style={{ ...label, marginBottom: 2 }}>To</label>
          <input style={{ ...input, width: 150 }} type="date" value={testEnd} onChange={(e) => setTestEnd(e.target.value)} />
        </div>
        <span style={{ fontSize: 12, color: '#9CA3AF', flex: 1, minWidth: 240 }}>
          Optional. Applies to preview and send-now only — scheduled runs are never affected.
        </span>
      </div>

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : alerts.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>
          No alerts yet. Create one to start sending scheduled business reports.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Alert</th>
                <th style={th}>Type</th>
                <th style={th}>Cadence</th>
                <th style={th}>Recipients</th>
                <th style={th}>Next covers</th>
                <th style={th}>Last sent</th>
                <th style={th}>On</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id}>
                  <td style={{ ...td, fontWeight: 600, color: '#111827' }}>
                    {a.name}
                    {a.consecutiveFailures > 0 && (
                      <div style={{ fontSize: 11, color: '#B91C1C', fontWeight: 500 }}>
                        {a.consecutiveFailures} failed send(s)
                      </div>
                    )}
                  </td>
                  <td style={td}>{a.alertTypeLabel}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{a.frequency.charAt(0) + a.frequency.slice(1).toLowerCase()}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                      {pad(a.sendHour)}:{pad(a.sendMinute)}<br />{a.timeZone}
                    </div>
                  </td>
                  <td style={td}>
                    {(a.recipients || []).map((r) => <span key={r} style={chip}>{r}</span>)}
                  </td>
                  <td style={td}>{a.nextPeriodLabel || '—'}</td>
                  <td style={td}>{fmtDateTime(a.lastSentAt)}</td>
                  <td style={td}>
                    <button
                      onClick={() => handleToggle(a)}
                      style={{
                        ...iconBtn,
                        width: 38, height: 21, borderRadius: 999, position: 'relative',
                        background: a.enabled ? '#F29213' : '#D1D5DB', transition: 'background .15s',
                      }}
                      aria-label={a.enabled ? 'Disable alert' : 'Enable alert'}
                    >
                      <span style={{
                        position: 'absolute', top: 2, left: a.enabled ? 19 : 3,
                        width: 17, height: 17, borderRadius: '50%', background: '#fff', transition: 'left .15s',
                      }} />
                    </button>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button style={iconBtn} title="Preview" disabled={busyRow === a.id} onClick={() => handlePreview(a)}>👁</button>
                    <button style={iconBtn} title="Send now" disabled={busyRow === a.id} onClick={() => handleSendNow(a)}>➤</button>
                    <button style={iconBtn} title="Edit" onClick={() => setEditing(a)}>✏️</button>
                    <button style={iconBtn} title="Delete" onClick={() => handleDelete(a)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 style={{ fontSize: 16, fontWeight: 800, color: '#111827', margin: '30px 0 12px' }}>Recent Dispatches</h2>
      {dispatches.length === 0 ? (
        <p style={{ color: '#9CA3AF', fontSize: 13 }}>
          Nothing has been dispatched yet. Scheduled sending stays off until it is explicitly enabled.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>When</th>
                <th style={th}>Alert</th>
                <th style={th}>Period</th>
                <th style={th}>Status</th>
                <th style={th}>Subject</th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((d) => {
                const style = STATUS_STYLE[d.status] || STATUS_STYLE.RUNNING;
                return (
                  <tr key={d.id}>
                    <td style={td}>{fmtDateTime(d.generatedAt || d.createdAt)}</td>
                    <td style={td}>{d.alertName || d.reportType}</td>
                    <td style={td}>
                      {String(d.periodStart || '').slice(0, 10)} → {String(d.periodEnd || '').slice(0, 10)}
                    </td>
                    <td style={td}>
                      <span style={{ background: style.bg, color: style.color, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                        {d.status}
                      </span>
                      {d.recipientCount > 0 && (
                        <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 6 }}>
                          {d.sentCount}/{d.recipientCount}
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={d.errorMessage || d.subject || ''}>
                      {d.errorMessage || d.subject || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}

      {editing && (
        <AlertDialog
          alert={editing.id ? editing : null}
          metadata={metadata}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAlerts(); }}
        />
      )}
      {previewHtml !== null && <PreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />}
    </div>
  );
}
